// src/App.js
import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ThemeProviderWrapper from "./ThemeProviderWrapper";
import { themes } from "./themes";
import { ResponsiveProvider } from "./contexts/ResponsiveContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Header from "./components/Header";
import AuthForm from "./components/AuthForm";
import ConfirmSignUp from "./components/ConfirmSignUp";
import Loading from "./components/Loading";
import {
  clearAuthState,
  isTokenExpired,
} from "./utils/authUtils";
import {
  preventAWSRedirects,
  clearAllAWSState,
} from "./utils/awsRedirectPrevention";
import AuthGuard from "./components/AuthGuard";
import ThemeBootstrapper from "./components/ThemeBootstrapper";
import CssBaseline from "@mui/material/CssBaseline";

// Portal landing + per-sibling landings
const PortalLanding = lazy(() => import("./components/PortalLanding"));
const CreativeLanding = lazy(() => import("./components/landings/CreativeLanding"));
const ResearchLanding = lazy(() => import("./components/landings/ResearchLanding"));
const SocialLanding = lazy(() => import("./components/landings/SocialLanding"));
const FounderLanding = lazy(() => import("./components/landings/FounderLanding"));
const BusinessDevLanding = lazy(() => import("./components/landings/BusinessDevLanding"));
const DocumentationLanding = lazy(() => import("./components/landings/DocumentationLanding"));
const AdminLanding = lazy(() => import("./components/landings/AdminLanding"));

// Prevent Safari swipe-back from accidentally leaving a deep route.
// Carried from the substrate; harmless when auth isn't active.
// eslint-disable-next-line no-unused-vars -- intentionally not rendered (disabled); see note at former usage site
const SwipeNavigationBlocker = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = React.useRef(location.pathname);
  const isBlockingRef = React.useRef(false);

  useEffect(() => {
    currentPathRef.current = location.pathname;
    isBlockingRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const publicPages = ["/login", "/register", "/confirm-signup"];
    const isOnPublicPage = publicPages.some(
      (page) => location.pathname === page,
    );
    if (isOnPublicPage) return;

    const handlePopState = () => {
      if (isBlockingRef.current) return;
      const targetPath = currentPathRef.current;
      if (window.location.pathname !== targetPath) {
        isBlockingRef.current = true;
        window.history.replaceState(null, "", targetPath);
        navigate(targetPath, { replace: true });
        setTimeout(() => {
          isBlockingRef.current = false;
        }, 100);
      }
    };

    window.history.pushState({ preventBack: true }, "", currentPathRef.current);
    window.addEventListener("popstate", handlePopState, { passive: true });
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      // Only honor a saved personal theme for AUTHENTICATED users; unauth visitors
      // get the brand default (refined by the no-store /site-theme.json fetch) so a
      // stale localStorage theme can never flash on first paint.
      const token = localStorage.getItem("jwtToken");
      const isAuthed = token && !isTokenExpired(token);
      if (isAuthed) {
        const savedTheme = localStorage.getItem("THEME");
        if (savedTheme) return JSON.parse(savedTheme);
      }
    } catch (e) {
      console.error("App.js - Error parsing saved theme:", e);
    }
    return themes.midnight;
  });

  useEffect(() => {
    fetch("/site-theme.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (t && t.primaryColor) handleThemeChange(t);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const cleanup = preventAWSRedirects();

    const existingToken = localStorage.getItem("jwtToken");
    if (existingToken && !isTokenExpired(existingToken)) {
      setIsAuthenticated(true);
    } else {
      clearAllAWSState();
      clearAuthState();
      setIsAuthenticated(false);
    }

    return cleanup;
  }, []);

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem("THEME", JSON.stringify(newTheme));
  };

  return (
    <Router>
      <ResponsiveProvider>
        <ThemeProviderWrapper theme={theme}>
          <DndProvider backend={HTML5Backend}>
            <CssBaseline />
            <ThemeBootstrapper
              isAuthenticated={isAuthenticated}
              onThemeLoaded={handleThemeChange}
            />
            {/* SwipeNavigationBlocker disabled: it blocked legitimate browser back-navigation and caused blank-page-on-back via bfcache. */}
            <AuthGuard>
              <Header
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
              />
              <Suspense fallback={<Loading />}>
                <Routes>
                  {/* Portal home */}
                  <Route path="/" element={<PortalLanding />} />

                  {/* Sibling-site landings (visual; internal clicks jump to the live sibling) */}
                  <Route path="/creative" element={<CreativeLanding />} />
                  <Route path="/research" element={<ResearchLanding />} />
                  <Route path="/social" element={<SocialLanding />} />
                  <Route path="/founder" element={<FounderLanding />} />
                  <Route path="/business-dev" element={<BusinessDevLanding />} />
                  <Route path="/documentation" element={<DocumentationLanding />} />
                  <Route path="/admin" element={<AdminLanding />} />

                  {/* Auth pages (carried from substrate) */}
                  <Route
                    path="/login"
                    element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                  />
                  <Route
                    path="/register"
                    element={<AuthForm setIsAuthenticated={setIsAuthenticated} />}
                  />
                  <Route path="/confirm-signup" element={<ConfirmSignUp />} />
                </Routes>
              </Suspense>
            </AuthGuard>
          </DndProvider>
        </ThemeProviderWrapper>
      </ResponsiveProvider>
    </Router>
  );
};

export default App;
